/**
 * landing-page-cinematic.tsx  —  /cinematic route
 *
 * Architecture:
 *   Uses the REAL DualPerspective and ServicesGrid components in normal document
 *   flow (no pinned viewport, no tall scroll runway). Each section gets a
 *   fly-in entry animation triggered on scroll (once: true, no scrub pin).
 *
 * Animation quality: AAA game-feel
 *   – Cards enter with elastic/overshoot easing (back.out / elastic.out)
 *   – Full 3-D depth: opacity + y + z + rotateX + scale (all spring to rest)
 *   – Stagger between cards creates a "settling waterfall" feel
 *   – Shine sweep fires once per card after it lands (premium feel)
 *   – Hover: 3-D tilt + radial cursor glow via useTilt hook
 *   – prefers-reduced-motion → plain fade, no tilt
 *
 * DO NOT pin, DO NOT change content / layout / colours.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import { Download, Smartphone } from 'lucide-react';

import {
  MoonGlowBackground,
  Hero,
  AIFeatures,
  DualPerspective,
  ServicesGrid,
  Pricing,
  Testimonials,
  CTA,
} from './landing-page';
import { Ceremonies } from '../components/landing-ceremonies';
import { LandingNav } from '../components/landing-nav';
import { LandingFooter } from '../components/landing-footer';

gsap.registerPlugin(ScrollTrigger);

// ---------------------------------------------------------------------------
// Service data — kept for the reduced-motion fallback PlainServicesGrid
// ---------------------------------------------------------------------------
const SERVICES = [
  { slug: 'editing',      title: 'Editing',      desc: 'Developmental, copy editing, and proofreading.' },
  { slug: 'design',       title: 'Design',        desc: 'Cover design, interior formatting, and illustrations.' },
  { slug: 'marketing',    title: 'Marketing',     desc: 'Launch strategies, ads, and author platforms.' },
  { slug: 'ghostwriting', title: 'Ghostwriting',  desc: 'Bring your ideas to life with professional writers.' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Fires a "shine sweep" overlay on a card right after it lands.
 * The sweep element self-removes after the animation ends.
 */
function triggerShineSweep(el: HTMLElement, delay = 0) {
  const shine = document.createElement('div');
  shine.className = 'card-shine-sweep';
  // Ensure parent clips the shine
  const prevOverflow = el.style.overflow;
  el.style.overflow = 'hidden';
  el.appendChild(shine);
  shine.style.animationDelay = `${delay}s`;
  shine.addEventListener('animationend', () => {
    shine.remove();
    el.style.overflow = prevOverflow;
  }, { once: true });
}

/**
 * Attach 3-D tilt + cursor-glow to a set of cards via plain DOM listeners.
 * Returns a cleanup function.
 * Automatically disabled on touch / reduced-motion.
 */
function attachTilt(
  cards: HTMLElement[],
  opts: { maxDeg?: number; hoverScale?: number; liftPx?: number; spring?: number } = {}
): () => void {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isTouch        = window.matchMedia('(hover: none)').matches;
  if (prefersReduced || isTouch || !cards.length) return () => {};

  const {
    maxDeg     = 8,
    hoverScale = 1.02,
    liftPx     = 4,
    spring     = 0.12,
  } = opts;

  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

  const cleanups: (() => void)[] = [];

  cards.forEach((el) => {
    let rotX = 0, rotY = 0, lift = 0, scale = 1;
    let tRotX = 0, tRotY = 0, tLift = 0, tScale = 1;
    let rafId = 0;
    let hovering = false;

    function animate() {
      rotX  = lerp(rotX,  tRotX,  spring);
      rotY  = lerp(rotY,  tRotY,  spring);
      lift  = lerp(lift,  tLift,  spring);
      scale = lerp(scale, tScale, spring);

      el.style.transform = `perspective(900px) rotateX(${rotX}deg) rotateY(${rotY}deg) translateY(${-lift}px) scale(${scale})`;

      const settled =
        Math.abs(rotX - tRotX) < 0.01 &&
        Math.abs(rotY - tRotY) < 0.01 &&
        Math.abs(lift - tLift) < 0.01 &&
        Math.abs(scale - tScale) < 0.001;

      if (!settled || hovering) {
        rafId = requestAnimationFrame(animate);
      } else {
        el.style.transform = '';
      }
    }

    function onMouseMove(e: MouseEvent) {
      const rect = el.getBoundingClientRect();
      const dx = (e.clientX - (rect.left + rect.width  / 2)) / (rect.width  / 2);
      const dy = (e.clientY - (rect.top  + rect.height / 2)) / (rect.height / 2);
      tRotX = -dy * maxDeg;
      tRotY =  dx * maxDeg;
      tLift  = liftPx;
      tScale = hoverScale;

      const px = ((e.clientX - rect.left) / rect.width)  * 100;
      const py = ((e.clientY - rect.top)  / rect.height) * 100;
      el.style.setProperty('--glow-x', `${px}%`);
      el.style.setProperty('--glow-y', `${py}%`);
      el.style.setProperty('--glow-opacity', '1');
    }

    function onMouseEnter() {
      hovering = true;
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(animate);
    }

    function onMouseLeave() {
      hovering = false;
      tRotX = tRotY = tLift = 0;
      tScale = 1;
      el.style.setProperty('--glow-opacity', '0');
      if (!rafId) rafId = requestAnimationFrame(animate);
    }

    // Inject glow styles
    el.classList.add('tilt-glow');
    el.style.setProperty('--glow-opacity', '0');
    el.style.transformStyle = 'preserve-3d';
    el.style.willChange     = 'transform';

    el.addEventListener('mouseenter', onMouseEnter);
    el.addEventListener('mousemove',  onMouseMove);
    el.addEventListener('mouseleave', onMouseLeave);

    cleanups.push(() => {
      cancelAnimationFrame(rafId);
      el.removeEventListener('mouseenter', onMouseEnter);
      el.removeEventListener('mousemove',  onMouseMove);
      el.removeEventListener('mouseleave', onMouseLeave);
      el.style.transform = '';
      el.style.transformStyle = '';
      el.style.willChange = '';
      el.classList.remove('tilt-glow');
    });
  });

  return () => cleanups.forEach((fn) => fn());
}

// ---------------------------------------------------------------------------
// AppDownloadSection — "Baixe o app" Android download area
// ---------------------------------------------------------------------------

// Stable public URL on our own domain — the CI overwrites this file on every
// build, so the link always serves the latest APK (repo stays private).
const APK_URL = 'https://reformu.lat/download/reformulat.apk';

/**
 * PhoneMock — a small CSS-only phone silhouette showing a stylized glimpse
 * of the editor + a lit candle in the corner (the "As cerimônias" motif),
 * so the download section has something to look at, not just a button.
 */
function PhoneMock() {
  return (
    <div
      className="relative mx-auto hidden shrink-0 md:block"
      style={{ width: 168, height: 336 }}
      aria-hidden
    >
      {/* bezel */}
      <div
        className="absolute inset-0 rounded-[28px]"
        style={{
          background: 'linear-gradient(160deg, #1B2A52, #0E1730 55%, #0A1024)',
          boxShadow: '0 0 0 1px rgba(168,184,232,0.22), 0 30px 70px -18px rgba(0,0,0,0.75)',
        }}
      />
      {/* screen */}
      <div
        className="absolute overflow-hidden rounded-[20px]"
        style={{ inset: 8, background: 'linear-gradient(180deg,#0C1326 0%,#080D1B 100%)' }}
      >
        {/* notch */}
        <div
          className="absolute left-1/2 top-2 -translate-x-1/2 rounded-full"
          style={{ width: 46, height: 6, background: 'rgba(168,184,232,0.25)' }}
        />
        {/* mock chapter title + prose skeleton */}
        <div className="absolute inset-x-4 top-9 flex flex-col gap-2.5">
          <span className="font-serif text-[11px] font-medium leading-tight" style={{ color: '#EBEEFA' }}>
            Capítulo IX
          </span>
          {[92, 78, 88, 60, 84, 70].map((w, i) => (
            <span
              key={i}
              className="block h-[3px] rounded-full"
              style={{ width: `${w}%`, background: 'rgba(168,184,232,0.18)' }}
            />
          ))}
        </div>
        {/* candle glow, bottom-right — ties back to the ceremonies motif.
            NOTE: inline `position` beats mock-skin.css's `.candle-mini{position:relative}`
            (same-specificity class rule that loads after Tailwind's `.absolute` utility). */}
        <span
          className="candle-mini"
          style={{ position: 'absolute', bottom: 24, right: 20, transform: 'scale(0.85)' }}
        >
          <span className="fl" style={{ position: 'absolute' }} />
          <span className="wk" />
          <span className="wx" />
        </span>
      </div>
    </div>
  );
}

function AppDownloadSection() {
  const { t } = useTranslation();
  return (
    <section
      id="download-app"
      className="py-16 md:py-24 relative overflow-hidden"
      style={{
        background:
          'linear-gradient(160deg, #0C1020 0%, #111828 60%, #0C1020 100%)',
      }}
    >
      {/* Subtle ambient glow */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(79,102,168,0.12) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center gap-10 md:flex-row md:items-center md:justify-center md:gap-16">
        <PhoneMock />

        <div className="flex flex-col items-center text-center gap-6 md:items-start md:text-left">
          {/* Icon badge */}
          <div
            className="flex items-center justify-center"
            style={{
              width: 64,
              height: 64,
              borderRadius: 18,
              background: 'rgba(79,102,168,0.14)',
              border: '1px solid rgba(79,102,168,0.35)',
              boxShadow: '0 0 36px rgba(79,102,168,0.22)',
            }}
          >
            <Smartphone size={30} style={{ color: '#9DB0E6' }} />
          </div>

          {/* Heading */}
          <h2
            className="font-serif font-bold leading-tight"
            style={{ fontSize: 'clamp(1.6rem, 4vw, 2.4rem)', color: '#DEE5F4' }}
          >
            {t('landing.appDownload.title', 'Leve a reformu.lat no bolso')}
          </h2>

          {/* Subtext */}
          <p
            className="leading-relaxed max-w-xl"
            style={{ fontSize: '1.05rem', color: 'rgba(222,229,244,0.58)' }}
          >
            {t(
              'landing.appDownload.subtitle',
              'Escreva, edite e acompanhe seu livro de qualquer lugar. O app Android se conecta diretamente ao site — sempre atualizado, sem precisar baixar atualizações.',
            )}
          </p>

          {/* Primary download button */}
          <a
            href={APK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="landing-sheen inline-flex items-center gap-3 font-semibold rounded-2xl transition-opacity hover:opacity-90 active:scale-95"
            style={{
              padding: '1rem 2rem',
              fontSize: '1rem',
              background: 'linear-gradient(135deg, #4F66A8 0%, #6377C2 100%)',
              color: '#FFFFFF',
              boxShadow: '0 4px 24px rgba(79,102,168,0.45)',
              textDecoration: 'none',
            }}
          >
            <Download size={20} />
            {t('landing.appDownload.cta', 'Baixar para Android (APK)')}
          </a>

          {/* Fine-print notes */}
          <div
            className="flex flex-col items-center gap-1.5 md:items-start"
            style={{ color: 'rgba(222,229,244,0.35)', fontSize: '0.8rem' }}
          >
            <span>{t('landing.appDownload.iosNote', 'iOS em breve')}</span>
            <span>
              {t(
                'landing.appDownload.permissionNote',
                'Requer "Instalar de fontes desconhecidas" habilitado nas configurações do Android',
              )}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Reduced-motion fallback — plain landing
// ---------------------------------------------------------------------------
function ReducedMotionFallback() {
  const navigate = useNavigate();
  const handleSignup = () => navigate('/app?auth=signup');

  const PlainServicesGrid = () => (
    <section className="py-10 md:py-20 bg-card" id="services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 md:mb-10">
          <h2 className="text-3xl font-serif mb-3 md:mb-4 text-primary">
            Everything you need to publish brilliantly.
          </h2>
          <p className="text-base md:text-lg text-muted-foreground">
            Discover specialized talent across every stage of the publishing journey.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-6">
          {SERVICES.map((service) => (
            <div key={service.slug} className="overflow-hidden rounded-xl bg-card border border-border">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={`/services/${service.slug}.webp`} alt={service.title} loading="lazy" className="h-full w-full object-cover" />
              </div>
              <div className="p-4">
                <h3 className="text-sm font-medium text-foreground">{service.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{service.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  return (
    <div className="landing-scope min-h-screen font-sans bg-background text-foreground">
      <MoonGlowBackground />
      <LandingNav />
      <main>
        <Hero onSignup={handleSignup} />
        <Ceremonies />
        <PlainServicesGrid />
        <AIFeatures onSignup={handleSignup} />
        <Pricing onSignup={handleSignup} />
        <Testimonials />
        <CTA onSignup={handleSignup} />
        <AppDownloadSection />
      </main>
      <LandingFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function LandingPageCinematic() {
  const navigate = useNavigate();
  const handleSignup = () => navigate('/app?auth=signup');

  const prefersReducedMotion =
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    // Collect all tilt cleanup functions to run on unmount
    const tiltCleanups: (() => void)[] = [];

    // ── 1. Lenis smooth scroll ────────────────────────────────────────────────
    const lenis = new Lenis({
      duration: 1.8,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 0.85,
    });
    lenis.on('scroll', ScrollTrigger.update);
    const lenisRafCb = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(lenisRafCb);
    gsap.ticker.lagSmoothing(0);

    // ── 2. MoonGlowBackground orb parallax ───────────────────────────────────
    const orbs = Array.from(
      document.querySelectorAll<HTMLElement>('[class*="blur-\\[72px\\]"]')
    ).slice(0, 3);
    const orbSpeeds = [-60, 80, -40];
    orbs.forEach((orb, i) => {
      gsap.to(orb, {
        y: orbSpeeds[i], ease: 'none',
        scrollTrigger: { trigger: wrapper, start: 'top top', end: 'bottom bottom', scrub: 1.5 },
      });
    });

    // ── 3. Hero parallax ─────────────────────────────────────────────────────
    const heroBgImg = wrapper.querySelector<HTMLElement>('img[src*="hero-bg"]');
    if (heroBgImg) {
      gsap.to(heroBgImg, {
        y: '22%', ease: 'none',
        scrollTrigger: { trigger: heroBgImg.closest('section'), start: 'top top', end: 'bottom top', scrub: true },
      });
    }

    // ── 4. DualPerspective — AAA "pop-through" entry ─────────────────────────
    //
    // #authors panel: from left with full 3D depth + overshoot.
    // #professionals: from right with slight delay.
    // Both get tilt on hover.
    //
    const howItWorks = document.getElementById('how-it-works');
    if (howItWorks) {
      const authorsPanel = document.getElementById('authors');
      const prosPanel    = document.getElementById('professionals');

      if (authorsPanel) {
        gsap.set(authorsPanel, {
          opacity: 0, x: -80, z: -200, rotateY: -18, scale: 0.88,
          transformPerspective: 1000,
        });
        gsap.to(authorsPanel, {
          opacity: 1, x: 0, z: 0, rotateY: 0, scale: 1,
          duration: 1.0, ease: 'back.out(1.8)',
          scrollTrigger: { trigger: howItWorks, start: 'top 82%', once: true },
          onComplete: () => triggerShineSweep(authorsPanel, 0),
        });
      }
      if (prosPanel) {
        gsap.set(prosPanel, {
          opacity: 0, x: 80, z: -200, rotateY: 18, scale: 0.88,
          transformPerspective: 1000,
        });
        gsap.to(prosPanel, {
          opacity: 1, x: 0, z: 0, rotateY: 0, scale: 1,
          duration: 1.0, ease: 'back.out(1.8)', delay: 0.12,
          scrollTrigger: { trigger: howItWorks, start: 'top 82%', once: true },
          onComplete: () => triggerShineSweep(prosPanel, 0),
        });
      }

      // 3-D tilt on both panels
      const dualCards = [authorsPanel, prosPanel].filter(Boolean) as HTMLElement[];
      tiltCleanups.push(attachTilt(dualCards, { maxDeg: 6, liftPx: 3 }));
    }

    // ── 5. ServicesGrid — depth burst + elastic stagger ───────────────────────
    //
    // Each card: opacity 0→1, y 60→0, z -220→0, rotateX -22→0, scale 0.86→1.
    // Easing: back.out(1.7) — overshoots slightly then settles. Game feel.
    //
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      // Set perspective on the grid container
      const svcGrid = servicesSection.querySelector<HTMLElement>('.grid');
      if (svcGrid) svcGrid.style.perspective = '1000px';

      const svcCards = Array.from(
        servicesSection.querySelectorAll<HTMLElement>('[class*="overflow-hidden rounded-xl"], [class*="overflow-hidden rounded-2xl"]')
      );
      if (svcCards.length) {
        gsap.set(svcCards, {
          opacity: 0, y: 60, z: -220, rotateX: -22, scale: 0.86,
          transformPerspective: 1000,
        });
        gsap.to(svcCards, {
          opacity: 1, y: 0, z: 0, rotateX: 0, scale: 1,
          stagger: { each: 0.10, from: 'start' },
          duration: 0.85, ease: 'back.out(1.7)',
          scrollTrigger: { trigger: servicesSection, start: 'top 82%', once: true },
          onComplete() {
            // Fire shine on each settled card
            svcCards.forEach((card, i) => {
              setTimeout(() => triggerShineSweep(card), i * 80);
            });
          },
        });

        // 3-D tilt hover
        tiltCleanups.push(attachTilt(svcCards, { maxDeg: 8 }));
      }
    }

    // ── 6. AIFeatures — elastic entry for desktop feature rows ───────────────
    const aiSection = wrapper.querySelector<HTMLElement>('#ai-features');
    if (aiSection) {
      // Header: blur fade (keep as-is — it's scrubbed, elegant)
      const aiHeader = aiSection.querySelector<HTMLElement>('.text-center.max-w-3xl');
      const aiGlows  = aiSection.querySelectorAll<HTMLElement>('.absolute.-top-40, .absolute.-bottom-40');

      if (aiHeader) {
        gsap.set(aiHeader, { opacity: 0, y: 60, filter: 'blur(8px)' });
        gsap.timeline({
          scrollTrigger: { trigger: aiSection, start: 'top 80%', end: 'top 30%', scrub: 1.0 },
        }).to(aiHeader, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 1, ease: 'power2.out' });
      }

      aiGlows.forEach((glow, i) => {
        gsap.to(glow, {
          y: i === 0 ? '-35%' : '35%', ease: 'none',
          scrollTrigger: { trigger: aiSection, start: 'top bottom', end: 'bottom top', scrub: true },
        });
      });

      // Desktop feature rows: elastic pop-in from depth
      const featureRows = Array.from(
        aiSection.querySelectorAll<HTMLElement>('.hidden.lg\\:grid .p-7.rounded-2xl')
      );
      if (featureRows.length) {
        gsap.set(featureRows, {
          opacity: 0, y: 50, z: -160, rotateX: -16, scale: 0.90,
          transformPerspective: 1000,
        });
        featureRows.forEach((row, i) => {
          gsap.to(row, {
            opacity: 1, y: 0, z: 0, rotateX: 0, scale: 1,
            duration: 0.80, ease: 'back.out(1.6)',
            scrollTrigger: { trigger: row, start: 'top 85%', once: true },
            delay: i * 0.06,
          });
        });
      }

      // Mobile AI cards: elastic from below
      const mobileAiCards = Array.from(
        aiSection.querySelectorAll<HTMLElement>('.lg\\:hidden .snap-center')
      );
      if (mobileAiCards.length) {
        gsap.set(mobileAiCards, {
          opacity: 0, y: 50, z: -120, rotateX: -14, scale: 0.90,
          transformPerspective: 800,
        });
        gsap.to(mobileAiCards, {
          opacity: 1, y: 0, z: 0, rotateX: 0, scale: 1,
          stagger: { each: 0.10 }, duration: 0.75, ease: 'back.out(1.6)',
          scrollTrigger: {
            trigger: aiSection.querySelector('.lg\\:hidden'),
            start: 'top 85%', once: true,
          },
        });
      }

      // Tilt on desktop feature row cards
      tiltCleanups.push(attachTilt(featureRows, { maxDeg: 5, liftPx: 3, spring: 0.10 }));
    }

    // ── 7. Pricing ──────────────────────────────────────────────────────────
    // Intentionally NOT touched by GSAP. The add-on cards, header and free-plan
    // banner already animate via Framer Motion `whileInView` directly inside the
    // <Pricing> component (see landing-page.tsx). Layering a second, independent
    // GSAP timeline on the very same DOM nodes (as this used to do) made both
    // engines fight over `transform`/`opacity` every frame — that fight, plus a
    // bouncy `back.out(1.7)` overshoot easing and 3-D tilt, was the "horrible"
    // pricing-card animation. Keep this section free of GSAP so the cards get a
    // single, seamless fade + rise instead.

    // ── 8. Testimonials — elastic 3-D entry ──────────────────────────────────
    const mainEl        = wrapper.querySelector<HTMLElement>('main');
    const allSections   = mainEl ? Array.from(mainEl.querySelectorAll<HTMLElement>('section')) : [];
    const testimSection = allSections.find(s => s.classList.contains('overflow-hidden'));

    if (testimSection) {
      const testimHeading = testimSection.querySelector<HTMLElement>('h2');
      const testimCards   = Array.from(
        testimSection.querySelectorAll<HTMLElement>('[class*="rounded-3xl"]')
      ).filter(el => el.closest('section') === testimSection);

      if (testimHeading) {
        gsap.set(testimHeading, { opacity: 0, y: 40, filter: 'blur(6px)' });
        gsap.to(testimHeading, {
          opacity: 1, y: 0, filter: 'blur(0px)',
          duration: 0.65, ease: 'power2.out',
          scrollTrigger: { trigger: testimSection, start: 'top 82%', once: true },
        });
      }

      if (testimCards.length) {
        // Set 3-D depth container
        const testimGrid = testimCards[0].parentElement;
        if (testimGrid) testimGrid.style.perspective = '1000px';

        // Alternating: left card comes from left-front, right from right-front
        testimCards.forEach((card, i) => {
          const fromX = i % 2 === 0 ? -70 : 70;
          gsap.set(card, {
            opacity: 0, x: fromX, z: -160, rotateY: i % 2 === 0 ? -12 : 12, scale: 0.88,
            transformPerspective: 1000,
          });
          gsap.to(card, {
            opacity: 1, x: 0, z: 0, rotateY: 0, scale: 1,
            duration: 0.90, ease: 'elastic.out(1, 0.65)',
            scrollTrigger: { trigger: testimSection, start: 'top 82%', once: true },
            delay: 0.1 + i * 0.12,
            onComplete: () => triggerShineSweep(card, 0),
          });
        });

        // 3-D tilt hover
        tiltCleanups.push(attachTilt(testimCards, { maxDeg: 7, liftPx: 5 }));
      }
    }

    // ── 9. CTA — scrubbed (keep as-is — good already) ───────────────────────
    const ctaSection = allSections[allSections.length - 1];
    if (ctaSection) {
      const ctaInner = ctaSection.querySelector<HTMLElement>('[class*="max-w"]');
      if (ctaInner) {
        gsap.set(ctaInner, { opacity: 0, y: 100, scale: 0.95, filter: 'blur(10px)' });
        gsap.timeline({
          scrollTrigger: { trigger: ctaSection, start: 'top 85%', end: 'top 25%', scrub: 1.0 },
        }).to(ctaInner, { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)', duration: 1, ease: 'power3.out' });
      }
    }

    // ── Cleanup ───────────────────────────────────────────────────────────────
    return () => {
      lenis.destroy();
      gsap.ticker.remove(lenisRafCb);
      ScrollTrigger.getAll().forEach((t) => t.kill());
      tiltCleanups.forEach((fn) => fn());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefersReducedMotion]);

  if (prefersReducedMotion) {
    return <ReducedMotionFallback />;
  }

  return (
    <div ref={wrapperRef} className="landing-scope min-h-screen font-sans bg-background text-foreground">
      <MoonGlowBackground />
      <LandingNav />
      <main>
        <Hero onSignup={handleSignup} />
        <Ceremonies />
        <DualPerspective />
        <ServicesGrid />
        <AIFeatures onSignup={handleSignup} />
        <Pricing onSignup={handleSignup} />
        <Testimonials />
        <CTA onSignup={handleSignup} />
        <AppDownloadSection />
      </main>
      <LandingFooter />
    </div>
  );
}
